import { useState } from "react";
import { cn } from "@utils/cn";
import {
  TwButton as Button,
  Dialog,
  Icon,
  Input,
  TwSelector as Selector,
  Text,
} from "@ui-component-library";
import { searchProps, getPropertyData } from "@services/property";
import { SkeletonLoader } from "@components/PropertySelector/components/SkeletonLoaders";
import { useSubscription } from "../../../context/SubscriptionCheckoutContext";

export default function SearchPropertyDialog({ isOpen, onClose, onConfirm }) {
  const { setAlertDialog } = useSubscription();
  const [selectedProperties, setSelectedProperties] = useState(new Map());
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSearch = async (value) => {
    if (value === "") return;

    setIsLoading(true);
    const result = await searchProps(value);

    const mappedResults = result.data.map((resultItem) => ({
      ...resultItem,
      beteckning: resultItem.beteckning.toLowerCase(),
    }));

    setSearchResults(
      mappedResults.sort((a, b) => a.beteckning.localeCompare(b.beteckning))
    );
    setIsLoading(false);
  };

  const handleSelect = (option) => {
    setSelectedProperties((prev) => {
      const selected = new Map(prev);
      if (selected.has(option.value)) {
        selected.delete(option.value);
      } else {
        selected.set(option.value, option.label);
      }
      return selected;
    });
  };

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      const refs = Array.from(selectedProperties.keys());
      const result = await getPropertyData(refs);

      const toSmallProperties = result.data.properties.filter((property) => property.to_small);
      const bigProperties = result.data.properties.filter((property) => property.prop_big_area);
      const otherProperties = result.data.properties.filter((property) => !property.to_small && !property.prop_big_area);

      if (toSmallProperties.length > 0) {
        setAlertDialog({
          title: 'En eller flera fastigheter är för små',
          subtitle:
            'Följande fastiget(er) kommer inte att kunna läggas till då de är mindre än den minsta storlek som krävs för att köpa plus',
          designation: toSmallProperties.map((p) => selectedProperties.get(p.lmID)).join(', '),

          helperInfo: 'Vänligen välj en annan fastighet - eller kontakta oss för rådgivning.'
        });

      }

      if (bigProperties.length > 0) {
        setAlertDialog({
          title: 'En eller flera fastigheter är för stor',
          subtitle:
            'Valda fastigheten är större än den största storlek som krävs för att köpa plus',
          designation: bigProperties.map((p) => selectedProperties.get(p.lmID)).join(', '),
          helperInfo: 'Vänligen välj en annan fastighet - eller kontakta oss för rådgivning.'
        });
      }

      if (result.ok && otherProperties.length > 0) {
        const properties = otherProperties.map((property) => ({
          ...property,
          designation: selectedProperties.get(property.lmID) ?? "",
        }));

        onConfirm({ properties });
      }
      onClose();
      setSearchResults(null);
      setSelectedProperties(new Map());
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog isOpen={isOpen} size="small" className="min-h-[458px] max-h-[780px]">
        <Dialog.Header>
          <div>
            <Text variant="text-lg/leading-normal/medium">
              Sök efter fastighet
            </Text>
            {/* <Text
              variant="text-sm/leading-normal/medium"
              className="text-[#737373]"
            >
              Lorem ipsum dolor sit amet consectetur adipisicing elit.
            </Text> */}
          </div>
          <Button variant="tertiary" size="icon" onClick={onClose}>
            <Icon
              tw
              name="CrossSimple"
              className="color-charcoal-500"
              size="large"
            />
          </Button>
        </Dialog.Header>
        <Dialog.Content className="pt-0">
          <div className={cn("bg-white-50 sticky top-0  py-2", searchResults?.length > 0 && "border-b border-solid border-[#E6E6E6] mb-2")}>
            <Input
              tw
              variant="fluid"
              name="search"
              label="Ange en fastighetsbeteckning"
              placeholder="Exempel: Sevedstorp 1:8"
              onChange={handleSearch}
              prefix={<Icon tw name="MagnifyingGlassSimple" size="medium" />}
            />
          </div>
          <div className="gap-1-5 flex w-full flex-col">
            <div className="flex flex-col gap-2">
              {isLoading && (
                <div className="mt-6 flex flex-col gap-3">
                  <SkeletonLoader length={10} type="searchResult" />
                </div>
              )}

              {!isLoading && searchResults?.length > 0 && (
                <Selector>
                  {searchResults.map((property) => (
                    <Selector.Option
                      key={property.objektidentitet}
                      label={property.beteckning}
                      value={property.registerenhet}
                      isSelected={selectedProperties.has(property.registerenhet)}
                      onClick={handleSelect}
                      className="capitalize"
                    />
                  ))}
                </Selector>
              )}

              {!isLoading && searchResults?.length === 0 && (
                <div className="mt-6 flex flex-col gap-3">
                  <Text variant="text-sm/leading-normal/medium" className="text-[#737373]">
                    Inga resultat hittades.
                  </Text>
                </div>
              )}
            </div>
          </div>
        </Dialog.Content>
        <Dialog.Footer>
          <Button
            variant="primary"
            fullWidth
            disabled={isLoading}
            onClick={handleConfirm}
          >
            Lägg till
          </Button>
        </Dialog.Footer>
      </Dialog>
    </>
  );
}
